"use client";

import { use, useState } from "react";
import Link from "next/link";
import useSWR from "swr";
import axios from "axios";
import {
  LuArrowLeft,
  LuBookOpen,
  LuChevronLeft,
  LuChevronRight,
  LuLoader,
  LuCompass,
  LuGlobe,
  LuWallet,
  LuGraduationCap,
  LuFileText,
  LuClipboardList,
  LuBuilding2,
  LuHouse,
  LuBriefcase,
  LuLandmark,
  LuNewspaper,
} from "react-icons/lu";
import {
  ArticleCard,
  ArticleCardSkeleton,
  type ArticleCardData,
} from "@/components/articles/ArticleCard";

const API = process.env.NEXT_PUBLIC_API_BASE_URL;
const fetcher = (url: string) => axios.get(url).then((r) => r.data);

const CATEGORY_ICON: Record<string, React.ElementType> = {
  "start-here": LuCompass,
  "countries": LuGlobe,
  "costs-and-budgeting": LuWallet,
  "scholarships-and-funding": LuGraduationCap,
  "visa-and-documents": LuFileText,
  "tests-and-preparation": LuClipboardList,
  "find-and-apply": LuBuilding2,
  "find-and-apply-to-universities": LuBuilding2,
  "student-life-abroad": LuHouse,
  "work-and-career": LuBriefcase,
  "pr-and-settlement": LuLandmark,
  "student-stories": LuBookOpen,
  "news-and-updates": LuNewspaper,
};

const LIMIT = 12;

export default function CategoryPage({
  params,
}: {
  params: Promise<{ "category-slug": string }>;
}) {
  const { "category-slug": categorySlug } = use(params);
  const [page, setPage] = useState(1);

  const { data, isLoading } = useSWR(
    `${API}/articles/category/${categorySlug}?page=${page}&limit=${LIMIT}`,
    fetcher,
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  const articles: ArticleCardData[] = data?.articles ?? [];
  const featuredArticle = data?.featuredArticle ?? null;
  const displayName: string = data?.displayName ?? categorySlug.replace(/-/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
  const description: string = data?.description ?? "";
  const total: number = data?.total ?? 0;
  const totalPages: number = data?.totalPages ?? 1;
  const relatedCategories: { key: string; urlSlug: string; displayName: string }[] = data?.relatedCategories ?? [];
  const categoryKey: string = data?.category ?? "";
  const Icon = CATEGORY_ICON[categoryKey] ?? LuBookOpen;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="border-b border-gray-200 dark:border-white/10 bg-gray-50/60 dark:bg-white/5">
        <div className="max-w-5xl mx-auto px-5 py-8">
          <Link
            href="/blog"
            className="inline-flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline mb-4"
          >
            <LuArrowLeft className="w-4 h-4" /> All blog posts
          </Link>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
              <Icon className="w-5 h-5" />
            </span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {displayName}
              </h1>
              {description && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {description}
                </p>
              )}
            </div>
          </div>
          {!isLoading && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
              {total} blog post{total !== 1 ? "s" : ""}
            </p>
          )}
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-5 py-10 space-y-10">
        {/* Featured article */}
        {featuredArticle && (
          <section>
            <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-3">
              Featured
            </p>
            <ArticleCard article={featuredArticle} featured />
          </section>
        )}

        {/* Article grid */}
        <section>
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: LIMIT }).map((_, i) => (
                <ArticleCardSkeleton key={i} />
              ))}
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">
              <LuBookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-base font-medium">No blog posts in this category yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || isLoading}
                className="p-2 rounded-lg border border-gray-200 dark:border-white/10 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <LuChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || isLoading}
                className="p-2 rounded-lg border border-gray-200 dark:border-white/10 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <LuChevronRight className="w-4 h-4" />
              </button>
              {isLoading && (
                <LuLoader className="w-4 h-4 animate-spin text-gray-400" />
              )}
            </div>
          )}
        </section>

        {/* Related categories */}
        {relatedCategories.length > 0 && (
          <section className="border-t border-gray-200 dark:border-white/10 pt-8">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
              Other Categories
            </h2>
            <div className="flex flex-wrap gap-2">
              {relatedCategories.map((cat) => {
                const CatIcon = CATEGORY_ICON[cat.key] ?? LuBookOpen;
                return (
                  <Link
                    key={cat.key}
                    href={`/blog/category/${cat.urlSlug}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    <CatIcon className="w-3.5 h-3.5" />
                    {cat.displayName}
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
